<template>
  <div id="content">
    <Navbar />
    <v-container class="py-8" fluid max-width="1200">
      <v-row justify="center">
        <v-col cols="12" md="8">
          <v-card class="pa-6 rounded-lg" elevation="2">
            <div class="text-center mb-6">
              <v-icon color="success" size="80">mdi-check-circle</v-icon>
              <h1 class="text-h4 mt-4">Payment Successful!</h1>
              <p class="text-body-1 mt-2">Thank you for your order</p>
            </div>

            <v-divider class="my-6"></v-divider>

            <!-- Order Summary -->
            <div class="mb-6">
              <h2 class="text-h5 mb-4">Order Details</h2>
              <v-row>
                <v-col cols="6">
                  <p class="text-body-1"><strong>Order Number:</strong></p>
                  <p class="text-body-1"><strong>Date:</strong></p>
                  <p class="text-body-1"><strong>Payment Method:</strong></p>
                  <p class="text-body-1"><strong>Payment ID:</strong></p>
                </v-col>
                <v-col cols="6">
                  <p class="text-body-1">{{ orderDetails.orderNumber }}</p>
                  <p class="text-body-1">{{ orderDetails.date }}</p>
                  <p class="text-body-1">{{ paymentMethod }}</p>
                  <p class="text-body-1">{{ transactionId }}</p>
                </v-col>
              </v-row>
            </div>

            <v-divider class="my-6"></v-divider>

            <!-- Payment Summary -->
            <div class="mb-6">
              <h2 class="text-h5 mb-4">Payment Summary</h2>
              <v-row>
                <v-col cols="6">
                  <p class="text-body-1"><strong>Subtotal:</strong></p>
                  <p class="text-body-1"><strong>Shipping:</strong></p>
                  <p class="text-body-1"><strong>Tax:</strong></p>
                  <p class="text-body-1"><strong>Total:</strong></p>
                </v-col>
                <v-col cols="6">
                  <p class="text-body-1">NPR {{ cartStore.subtotal }}</p>
                  <p class="text-body-1">NPR {{ cartStore.shippingFee }}</p>
                  <p class="text-body-1">NPR {{ cartStore.taxAmount }}</p>
                  <p class="text-body-1 font-weight-bold">
                    NPR {{ cartStore.grandTotal }}
                  </p>
                </v-col>
              </v-row>
            </div>

            <v-divider class="my-6"></v-divider>

            <!-- Customer Information -->
            <div class="mb-6">
              <h2 class="text-h5 mb-4">Customer Information</h2>
              <v-row>
                <v-col cols="6">
                  <p class="text-body-1"><strong>Name:</strong></p>
                  <p class="text-body-1"><strong>Phone:</strong></p>
                  <p class="text-body-1"><strong>Email:</strong></p>
                  <p class="text-body-1"><strong>Address:</strong></p>
                </v-col>
                <v-col cols="6">
                  <p class="text-body-1">{{ shippingAddress.fullName }}</p>
                  <p class="text-body-1">{{ shippingAddress.phone }}</p>
                  <p class="text-body-1">{{ shippingAddress.email }}</p>
                  <p class="text-body-1">{{ shippingAddress.province }}</p>
                  <!-- <p class="text-body-1">{{ shippingAddress.phone }}</p>
                  <p class="text-body-1">
                    {{ shippingAddress.street }}, Ward
                    {{ shippingAddress.ward }},
                    {{ shippingAddress.municipality }},
                    {{ shippingAddress.district }},
                    {{ shippingAddress.province }} -->
                  <!-- </p> -->
                </v-col>
              </v-row>
            </div>

            <!-- Ordered Items -->
            <div class="mb-6">
              <h2 class="text-h5 mb-4">Ordered Items</h2>
              <v-table>
                <thead>
                  <tr>
                    <th>Product</th>
                    <th>Quantity</th>
                    <th>Price</th>
                    <th>Total</th>
                  </tr>
                </thead>
                <tbody>
                  <!-- {{ cartStore.cart }} -->
                  <tr v-for="item in cartStore.cart" :key="item.id">
                    <td>{{ item.title }}</td>
                    <td>{{ item.quantity }}</td>
                    <td>{{ item.current_price }}</td>
                    <td>{{ item.subTotal }}</td>
                  </tr>
                </tbody>
              </v-table>
            </div>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useCartStore } from '@/Store/cartStore';
import axios from "axios";
import { apiConfig } from "@/config/apiConfig";
import OrderSummary from '../components/SharedUI/OrderSummary.vue';
import Navbar from '../components/Navbar.vue';

// State variables
const cartStore = useCartStore();
const paymentMethod = ref('esewa');
const transactionId = computed(() => `TXN-${Date.now()}-${Math.floor(Math.random() * 1000)}`);
const orderDetails = ref({
  orderNumber: `ORD-${Date.now()}`,
  date: new Date().toLocaleDateString(),
});

// const shippingAddress = localStorage.getItem('shippingAddress');
const shippingAddress = computed(() => {
  const address = localStorage.getItem('shippingAddress');
  return address ? JSON.parse(address) : null;
});
// API Call to Submit Order Data
const submitOrderData = async () => {
  try {
    // Retrieve and parse the shipping address from localStorage
    const shippingAddress = JSON.parse(localStorage.getItem('shippingAddress'));

    // Extract the necessary fields from the shipping address
    const { fullName, phone,email, province, district, municipality, ward, street, note } = shippingAddress;

    // Prepare the order data
    const orderData = {
      is_paid: true,
      payment_provider: paymentMethod.value,
      transaction_id: transactionId.value,
      payment_method: "online",
      order_items: cartStore.cart.map(item => ({
        service_detail_id: item.id,
        quantity: item.quantity,
        price: item.current_price
      })),
        name: fullName,
        contact: phone,
        email: email,
        province: province,
        district: district,
        municipality: municipality,
        ward: ward,
        street: street,
        notes: note
    };

    // Send the order data to your API endpoint
    const response = await fetch(`${apiConfig.baseUrl}${apiConfig.verifyOrder}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(orderData),
    });

    const data = await response.json();
    // console.log('Order submitted successfully:', data);
  }catch (error) {
    console.error('Error submitting order:', error);
  }
};

// Call submitOrderData before rendering the confirmation page
onMounted(() => {
  submitOrderData();
});
</script>
